/**
 * The staffing module's WRITE seam. Kept apart from `api.ts` so a page that only
 * displays cannot reach a POST by autocomplete.
 *
 * Every function here mirrors a route in
 * `apps/customer-management-api/src/app/api/staffing/*` and, importantly,
 * mirrors its FAILURE shapes too. Those routes were written to refuse things
 * loudly and by name — a pin to a customer the CRM does not have, a config the
 * engine would later reject — and a client that collapses all of that into
 * "something went wrong" throws away the only part the user can act on.
 */
import { apiFetch, ApiError } from '@/features/crm/lib/api-fetch';
import type {
  StaffingConfigDto,
  StoredTierDto,
  SnapshotSummaryDto,
} from './api';
import type { SnapshotRequest } from './lib/snapshot';

/**
 * A rejection the server explained. `problems` carries cm-api's full list rather
 * than the first item: a config version is IMMUTABLE, so someone fixing one
 * should not discover the remaining faults one round-trip at a time.
 */
export class StaffingWriteError extends Error {
  readonly status: number;
  readonly problems: string[];
  readonly remedy: string | null;

  constructor(
    message: string,
    status: number,
    problems: string[] = [],
    remedy: string | null = null,
  ) {
    super(message);
    this.name = 'StaffingWriteError';
    this.status = status;
    this.problems = problems;
    this.remedy = remedy;
  }
}

/** Unwrap cm-api's error bodies into something a form can render. */
function asWriteError(err: unknown, fallback: string): StaffingWriteError {
  // Already ours — re-wrapping would keep the message but drop `problems` and
  // `remedy`, which are the parts that tell a caller what to DO.
  if (err instanceof StaffingWriteError) return err;
  if (err instanceof ApiError) {
    const body =
      err.body && typeof err.body === 'object'
        ? (err.body as {
            error?: string;
            problems?: string[];
            remedy?: string;
          })
        : null;
    return new StaffingWriteError(
      body?.error ?? err.message ?? fallback,
      err.status,
      Array.isArray(body?.problems) ? body.problems : [],
      body?.remedy ?? null,
    );
  }
  return new StaffingWriteError(
    err instanceof Error ? err.message : fallback,
    0,
  );
}

// ── pins ────────────────────────────────────────────────────────────────────

export interface PinRequest {
  customerId: string;
  /** The `staffing_csm` ROW id, not the engine key. See lib/model.ts. */
  csmId: string;
  reason?: string | null;
}

/**
 * Pin a customer to a CSM.
 *
 * 404 is a real answer here, not a bug: cm-api checks the customer exists in
 * `crm.customer_settings` before writing, because a pin whose key matches no
 * account survives every later check, is ignored by assignment, and never shows
 * up in coverage — persistence storing a rule that does nothing.
 */
export async function pinCustomer(req: PinRequest): Promise<void> {
  try {
    await apiFetch('/api/staffing/pins', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(req),
    });
  } catch (err) {
    throw asWriteError(err, 'Could not pin that customer.');
  }
}

export async function unpinCustomer(customerId: string): Promise<void> {
  try {
    await apiFetch(
      `/api/staffing/pins?customerId=${encodeURIComponent(customerId)}`,
      { method: 'DELETE' },
    );
  } catch (err) {
    throw asWriteError(err, 'Could not remove that pin.');
  }
}

// ── config ──────────────────────────────────────────────────────────────────

export interface PublishConfigInput {
  tiers: StoredTierDto[];
  hrsPerFteYear: number;
  balanceBy: 'count' | 'spend';
  guardrailBasis: 'profit' | 'revenue';
  guardrailPct: number;
  revenuePct: number;
  profitPct: number;
  /**
   * Team-wide incentive as a PERCENTAGE of base salary (0-100). Optional: a
   * version written before this existed recorded none, and the server defaults
   * it to 0 rather than inventing one.
   */
  incentivePct?: number;
  note?: string | null;
}

/**
 * Publish a new configuration version.
 *
 * Versions are IMMUTABLE — this inserts a row and the highest id wins — so there
 * is no update path and nothing is overwritten. The previous version stays
 * readable, which is what makes the config an audit trail rather than a setting.
 *
 * REQUIRES `view:staffing_financials` AS WELL AS `manage:staffing`, enforced
 * server-side. That is not an oversight to route around: a config carries the
 * cost model, the redacted read OMITS `loadedCostPerFte`, and a caller who
 * cannot see the costs would round-trip every tier's cost silently to null.
 * `canPublishConfig()` below is the client-side half of the same rule.
 */
export interface PublishResult {
  version: number;
  /**
   * Team seats copied from the previous version into this one.
   *
   * Slots are scoped to a config version, so a publish that carried none is
   * either the very first version or a bug — and the difference matters,
   * because a version with no seats is a version in which nobody staffs
   * anything and the whole book reads as unallocated.
   */
  slotsCarriedForward: number;
}

export async function publishConfig(
  input: PublishConfigInput,
): Promise<PublishResult> {
  try {
    // The route answers `{ config: {...}, slots: { carriedForward } }`, NOT a
    // bare `{ version }`. This read used to be `{ version }` — and every unit
    // test agreed, because the mock was written from this file rather than from
    // the route. The result was `undefined` rendered into "Published version
    // undefined" after a write that had actually succeeded.
    const body = await apiFetch<{
      config: { version: number };
      slots?: { carriedForward?: number };
    }>('/api/staffing/config', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    const version = body?.config?.version;
    if (typeof version !== 'number') {
      // The write may well have succeeded; what failed is our ability to say
      // WHICH version it produced. Saying so beats rendering "version undefined"
      // beside a green checkmark.
      throw new StaffingWriteError(
        'The configuration was submitted, but cm-api did not return a version number. ' +
          'Reload before publishing again — a second publish would add another version.',
        0,
      );
    }
    return {
      version,
      slotsCarriedForward: body.slots?.carriedForward ?? 0,
    };
  } catch (err) {
    throw asWriteError(err, 'Could not publish that configuration.');
  }
}

/**
 * Whether publishing is possible AT ALL for this reader, and why not when it is
 * not.
 *
 * Keyed on the SERVER's answer — a redacted read means the financial keys are
 * absent, so the round-trip would null every tier cost, and cm-api refuses it
 * with a 403. Disabling the control with that reason shown is better than
 * offering a button that always fails.
 */
export function canPublishConfig(
  config: StaffingConfigDto | null,
  canManage: boolean,
): { allowed: boolean; reason: string | null } {
  if (!config) {
    return {
      allowed: false,
      reason: 'There is no configuration to change yet.',
    };
  }
  if (!canManage) {
    return {
      allowed: false,
      reason: 'Publishing a version needs the manage:staffing authority.',
    };
  }
  if (config.financialsRedacted) {
    return {
      allowed: false,
      reason:
        'Publishing needs view:staffing_financials too. A configuration carries the cost model, and this view omits it — saving from here would clear every tier cost.',
    };
  }
  return { allowed: true, reason: null };
}

/**
 * The saved config, as the shape POST expects. Drops server-owned fields.
 *
 * REFUSES a redacted config rather than defaulting its financial keys.
 *
 * The first version wrote `config.guardrailPct ?? 0`, reasoning that
 * `canPublishConfig()` had already ruled the redacted case out. That reasoning
 * holds today and is still the wrong shape: absent keys would have become
 * `0` — a guardrail of zero percent, a revenue share of zero — and the request
 * would have been accepted, stored as an IMMUTABLE version, and read back as
 * though someone had chosen those numbers. A guard one caller happens to apply
 * is not the same as a value that cannot be invented, and the whole financial
 * model would have been silently zeroed by a caller who simply could not see it.
 */
export function toPublishInput(
  config: StaffingConfigDto,
  overrides: Partial<PublishConfigInput> = {},
): PublishConfigInput {
  if (
    config.guardrailPct === undefined ||
    config.revenuePct === undefined ||
    config.profitPct === undefined
  ) {
    throw new StaffingWriteError(
      'This view has the cost model redacted, so it cannot publish a configuration — ' +
        'the financial percentages are absent here and publishing would store zeros for them.',
      403,
    );
  }
  return {
    tiers: config.tiers,
    hrsPerFteYear: config.hrsPerFteYear,
    balanceBy: config.balanceBy,
    guardrailBasis: config.guardrailBasis,
    guardrailPct: config.guardrailPct,
    revenuePct: config.revenuePct,
    profitPct: config.profitPct,
    // Carried forward, so publishing a THRESHOLD change does not silently reset
    // the team's incentive to zero. `?? 0` is right here and not a papered-over
    // null: the server defaults an unrecorded incentive to 0 too, so this
    // round-trips a version written before the field existed unchanged.
    incentivePct: config.incentivePct ?? 0,
    note: config.note,
    ...overrides,
  };
}

// ── team ────────────────────────────────────────────────────────────────────

/**
 * Set (or clear) one person's BASE salary.
 *
 * The base, not the cost: the annual cost is derived from this plus the
 * team-wide incentive, and is never written anywhere.
 *
 * The whole roster row goes back, because `POST /api/staffing/roster` is an
 * upsert over the record rather than a patch of one field — but note what that
 * means for the salary specifically: an ABSENT `baseSalaryAnnual` is "leave it
 * alone", so this function only ever sends the key when it is deliberately
 * setting one. Sending `undefined` from a redacted read is how a rename would
 * otherwise erase a salary.
 */
export async function setCsmSalary(
  member: {
    id: string;
    displayName: string;
    externalId: string | null;
    email: string | null;
    role: string;
    active: boolean;
  },
  baseSalaryAnnual: number | null,
): Promise<void> {
  try {
    await apiFetch('/api/staffing/roster', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...member, baseSalaryAnnual }),
    });
  } catch (err) {
    throw asWriteError(err, 'Could not save that salary.');
  }
}

/**
 * Write the team's seats.
 *
 * REPLACES the whole slot list for the current config version — that is the
 * route's contract, and it is the right one: seats are ordered and the engine
 * breaks capacity ties by FIRST slot, so a per-seat patch API would let two
 * concurrent edits interleave into an order neither caller asked for.
 *
 * The caller therefore sends the list it wants to EXIST, not a delta.
 */
export async function saveSlots(
  slots: readonly {
    tierKey: string;
    position: number;
    csmId: string | null;
  }[],
): Promise<{ slotsWritten: number }> {
  try {
    const body = await apiFetch<{ slotsWritten?: number }>(
      '/api/staffing/slots',
      {
        // PUT, not POST. The route has only ever exported GET and PUT — this
        // sent POST from the day it shipped, and the deployment answers an
        // unimplemented method with a bare 404 and no JSON body, so the client
        // rendered the generic "That wasn't found (404)" and nothing pointed at
        // the method. Every seat write failed: removing a team member with any
        // seats, and any tier change made from the Team tab. Removing somebody
        // who staffed nothing "worked", because that path skips this call
        // entirely — which is why it looked intermittent.
        //
        // PUT is also the correct verb here and the reason the route chose it:
        // the body is the WHOLE seat list, replacing what is stored.
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slots }),
      },
    );
    return { slotsWritten: body?.slotsWritten ?? 0 };
  } catch (err) {
    throw asWriteError(err, 'Could not save the team.');
  }
}

/**
 * Add someone to the roster.
 *
 * The roster is the ALLOW-LIST of people work may be assigned to, not a
 * directory: the engine refuses a pin naming anyone who is not on it, precisely
 * so a stale or malformed rule cannot invent a person and hand them a book. So
 * adding a row is the act that makes someone assignable, and it is the reason
 * this needs `manage:staffing` and not merely an edit box.
 *
 * `externalId` OR `email` is required, and that is not a form nicety. The roster
 * joins to `customer_settings.csm_external_id` / `csm_email`; a display name is
 * never a join key, because "Taylor R. Daily" and "Taylor Daily" compare unequal
 * and empty a book. A row with neither identifier could never own an account —
 * it would look like a colleague and behave like a decoration. cm-api refuses it
 * too; this is the same rule stated early enough to be fixable in the form.
 */
export async function addCsm(member: {
  displayName: string;
  role: 'manager' | 'csm';
  externalId: string | null;
  email: string | null;
}): Promise<{ id: string }> {
  try {
    const body = await apiFetch<{ csm?: { id?: string } }>(
      '/api/staffing/roster',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // `baseSalaryAnnual` is deliberately ABSENT rather than null. The route
        // treats an absent key as "not mentioned", and sending null would record
        // a deliberate "we know this person's salary and it is unrecorded" on a
        // row nobody has costed yet. It also keeps this callable by a manager
        // without view:staffing_financials, who would otherwise be refused for
        // mentioning a salary they cannot see.
        body: JSON.stringify(member),
      },
    );
    const id = body?.csm?.id;
    if (typeof id !== 'string') {
      throw new StaffingWriteError(
        'The roster entry was submitted, but cm-api did not return its id. ' +
          'Reload before adding again — a second attempt would create a duplicate.',
        0,
      );
    }
    return { id };
  } catch (err) {
    throw asWriteError(err, 'Could not add that person to the roster.');
  }
}

/**
 * Deactivate or restore a roster entry.
 *
 * THERE IS NO DELETE, by design on the server: pins and snapshots reference
 * these rows, so a hard delete would either be blocked by the foreign key or
 * take the audit trail with it. "Remove" therefore means DEACTIVATE — the person
 * stops appearing on the team and stops being assignable, and every past
 * scenario that named them still reads correctly.
 *
 * That also makes it reversible, which is the only reason it is safe to offer as
 * a one-click action at all.
 *
 * SEATS ARE NOT CLEARED HERE. The caller does that first, deliberately: a slot
 * pointing at a deactivated person is the one genuinely bad state — the engine
 * distributes to whoever holds a seat and does not consult `active`, so the
 * departed keep receiving customers. Doing it in this order fails safe; the
 * worst outcome of a half-completed removal is somebody active with no seats,
 * which is visible on the page rather than silent in the model.
 */
export async function setCsmActive(
  member: {
    id: string;
    displayName: string;
    externalId: string | null;
    email: string | null;
    role: string;
  },
  active: boolean,
): Promise<void> {
  try {
    await apiFetch('/api/staffing/roster', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // Again no `baseSalaryAnnual`: absent means "leave it alone". Sending the
      // key from a redacted read would erase a salary as a side effect of
      // removing someone, and sending it from an unredacted one would make this
      // fail for a manager who cannot see salaries.
      body: JSON.stringify({
        id: member.id,
        displayName: member.displayName,
        externalId: member.externalId,
        email: member.email,
        role: member.role,
        active,
      }),
    });
  } catch (err) {
    throw asWriteError(
      err,
      active
        ? 'Could not restore that person.'
        : 'Could not remove that person from the team.',
    );
  }
}

// ── first run ───────────────────────────────────────────────────────────────

/**
 * What bootstrapping actually did, as cm-api reports it.
 *
 * Every field is a COUNT of something created rather than a success flag,
 * because the interesting outcomes here are partial: a config that already
 * existed, seats that were already present, advisors whose identity could not be
 * resolved. "It worked" would hide all of them.
 */
// ── snapshots ───────────────────────────────────────────────────────────────

/**
 * Freeze the current scenario as a snapshot.
 *
 * ONE failure here deserves its own sentence, and cm-api gives it a 409: the
 * book changed between the page load and the save. The daily Domo sync moves
 * spend, so a page left open since this morning may be proposing an assignment
 * over a book that no longer exists, and storing that would record fiction. The
 * remedy is a reload, not a retry, so this says so.
 */
export async function saveSnapshot(
  body: SnapshotRequest,
): Promise<SnapshotSummaryDto> {
  try {
    const res = await apiFetch<{ snapshot: SnapshotSummaryDto }>(
      '/api/staffing/snapshots',
      { method: 'POST', body: JSON.stringify(body) },
    );
    return res.snapshot;
  } catch (err) {
    const wrapped = asWriteError(err, 'Could not save this scenario.');
    if (wrapped.status === 409) {
      throw new StaffingWriteError(
        'The customer book changed while this scenario was open, so it was not saved — the figures here were computed against a book that no longer exists.',
        409,
        wrapped.problems,
        'Reload the staffing book and recompute before saving.',
      );
    }
    throw wrapped;
  }
}

export interface BootstrapReport {
  rosterCreated: number;
  rosterUpdated: number;
  configVersion: number | null;
  configCreated: boolean;
  slotsCreated: number;
  slotsAlreadyPresent: number;
  /** Derived seats whose CSM key matched no roster row. Should be empty. */
  unmatchedSlotKeys: string[];
  identityWarnings: {
    /** One display name resolving to more than one stable id — kept separate. */
    ambiguousNames: string[];
    /** One email carrying more than one external id — contradictory source data. */
    conflictingEmails: string[];
  };
}

/**
 * Seed the staffing model from the CRM — the module's FIRST RUN.
 *
 * Derives the roster, a first config version and the current team from
 * `crm.customer_settings` rather than from a committed seed file: the CRM
 * already holds those facts, and a file would be a stale second copy of them
 * that also put the real roster in the repository.
 *
 * SAFE TO REPEAT. cm-api creates the config only when none exists and the seats
 * only when that version has none, because a second config version is a real
 * audit event and overwriting a team would silently discard a plan somebody
 * made. So this is additive on a fresh module and close to a no-op afterwards —
 * which is why the report counts things rather than claiming success.
 *
 * NEEDS BOTH `manage:staffing` AND `view:staffing_financials`, enforced
 * server-side. Bootstrapping writes a config version, and a config carries the
 * cost model — the same rule that stops a caller who cannot see the figures from
 * authoring them. A caller holding only the first gets a 403 that says so.
 */
export async function bootstrapStaffing(): Promise<BootstrapReport> {
  try {
    return await apiFetch<BootstrapReport>('/api/staffing/bootstrap', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    });
  } catch (err) {
    throw asWriteError(err, 'Could not seed the staffing model from the CRM.');
  }
}
